// ── capstone-helpers: module access + pipeline taxonomy + schema state (Wave 8) ─
// CONTRACT TRUTH (verified against apps/vjal-coaching-api/src):
//   - The ONLY capstone route is POST /cohorts/:id/capstone-submissions
//     (authorizeCoaching(manage.sessions) + global mutation scope = STAFF), which
//     returns 202 and enqueues a vjal_agent `capstone.evaluate` outbox intent
//     (DARK / dry_run while the report-agent gate is closed).
//   - There is NO participant-scoped capstone-submit route → the participant
//     capstone upload is a BACKEND GAP (the Wave-9 participant surface ships an
//     honest "not available until a participant submit route exists" state).
//   - There is NO capstone LIST/GET route → the admin submissions list has no
//     backend read yet; the list renders an honest not-configured state and NO
//     read fires.
//   - The structured capstone JSON schema is an OPEN DEPENDENCY → render
//     pending_schema as MATURITY-GATED; store/display schemaStatus/version/
//     reference only; DO NOT invent schema fields.
//
// ── PARTICIPANT EXCLUSION (Wave 8 release gate) ──────────────────────────────
// A `participant` is DENIED the ADMIN capstone module (route + page fail-closed).
// Fail-closed allow-list: an unknown/new role is denied until explicitly added.

import { humanizeLabel } from "@vjal/ui";

import type { CoachingRole } from "../../domain/roles";
import type { HonestPosture } from "../../components/honest-state";

// MUST equal the route manifest's capstoneModuleRoles.
export const CAPSTONE_MODULE_ROLES: readonly CoachingRole[] = [
  "super_admin",
  "admin",
  "coach",
  "company_admin",
  "cohort_lead"
];

export function canAccessCapstoneModule(role: CoachingRole): boolean {
  return CAPSTONE_MODULE_ROLES.includes(role);
}

// Only STAFF (manage.sessions + global scope) can perform the intake POST. The
// non-staff module roles are read-only observers of their scoped cohort.
export function canSubmitCapstoneIntake(role: CoachingRole): boolean {
  return role === "super_admin" || role === "admin";
}

// ── Submission STATUS pipeline (the backend's intake → evaluation lifecycle) ──
// These are the EXACT status values the capstone repository persists
// (evaluation_enqueued → evaluation_submitted → evaluated / failed). We normalize
// for display + tone but never invent a status the server didn't define. The
// certificate-trigger stage is NOT a backend status (no certificate table) — it
// is rendered as a downstream NOT-CONFIGURED stage, never as a real status.
export type CapstoneStatusKind = "enqueued" | "processing" | "evaluated" | "failed";

const STATUS_ALIASES: Record<string, CapstoneStatusKind> = {
  evaluation_enqueued: "enqueued",
  enqueued: "enqueued",
  queued: "enqueued",
  evaluation_submitted: "processing",
  submitted: "processing",
  processing: "processing",
  evaluating: "processing",
  evaluated: "evaluated",
  completed: "evaluated",
  failed: "failed",
  error: "failed"
};

export function normalizeCapstoneStatus(status: string | null | undefined): CapstoneStatusKind {
  if (!status) return "enqueued";
  return STATUS_ALIASES[status] ?? "processing";
}

export function capstoneStatusLabel(status: string | null | undefined): string {
  return humanizeLabel(status ?? "evaluation_enqueued");
}

export function capstoneStatusTone(status: string | null | undefined): "success" | "warning" | "danger" | "default" {
  switch (normalizeCapstoneStatus(status)) {
    case "evaluated":
      return "success";
    case "failed":
      return "danger";
    case "processing":
    case "enqueued":
      return "warning";
    default:
      return "default";
  }
}

// ── The capstone PROCESSING PIPELINE (static contract reference, not data) ───
// The four product stages, each carrying the HONEST posture for its lane today.
// This is a documentation surface (the contract's pipeline), NOT fabricated
// submission data: upload/intake is the STAFF POST (available); conversion +
// evaluation run on the DARK agent lane (processing/unavailable until the gate
// flips); the certificate trigger has NO backend (not-configured).
export interface CapstonePipelineStage {
  id: string;
  label: string;
  description: string;
  posture: HonestPosture;
}

export const CAPSTONE_PIPELINE: readonly CapstonePipelineStage[] = [
  {
    id: "upload",
    label: "Upload / intake",
    description:
      "A staff intake records the capstone artifact (Excel/Word/PDF/PPT reference or text) and enqueues evaluation. Participant self-submit has no route yet (backend gap).",
    posture: "gated"
  },
  {
    id: "conversion",
    label: "Conversion",
    description:
      "Artifact conversion runs on the agent lane. That lane is operator-gated (dark), so conversion is unavailable until it's enabled — no fabricated conversion status is shown.",
    posture: "unavailable"
  },
  {
    id: "evaluation",
    label: "Evaluation",
    description:
      "The agent capstone.evaluate step scores the artifact. The report/capstone agent lane is dark, so evaluation is processing/unavailable until the gate flips.",
    posture: "unavailable"
  },
  {
    id: "certificate-trigger",
    label: "Certificate trigger",
    description:
      "A passing evaluation would trigger a certificate. There is no certificate backend yet, so this stage is not configured — nothing is issued.",
    posture: "not-configured"
  }
];

// ── pending_schema (the structured capstone JSON schema — OPEN DEPENDENCY) ────
// The structured-JSON schema is unresolved. We surface ONLY the schema STATUS
// (pending_schema), an optional version, and a reference — never invented schema
// fields. Rendered as MATURITY-GATED.
export interface CapstoneSchemaState {
  status: "pending_schema";
  version: string | null;
  reference: string | null;
}

export const CAPSTONE_SCHEMA_STATE: CapstoneSchemaState = {
  status: "pending_schema",
  version: null,
  reference: null
};
